/**
 * Invitation React Query Hooks
 *
 * React Query hooks for company invitation operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type { CreateInvitationDto } from '@lognai/plugin-admin';
import type { PaginatedListResponse } from '../api/client';
import * as invitationApi from '../api/invitations';
import type { EnrichedInvitation, InvitationQueryParams } from '../api/invitations';
import { invitationKeys, membershipKeys } from './query-keys';

/**
 * Get list of invitations for a company
 */
export const useInvitations = (
  companyId: string,
  params?: InvitationQueryParams,
  options?: Omit<
    UseQueryOptions<PaginatedListResponse<EnrichedInvitation>>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    queryKey: invitationKeys.list(companyId, params),
    queryFn: () => invitationApi.listInvitations(companyId, params),
    enabled: !!companyId,
    ...options,
  });
};

/**
 * Get a single invitation by ID
 */
export const useInvitation = (
  companyId: string,
  invitationId: string,
  options?: Omit<UseQueryOptions<EnrichedInvitation>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: invitationKeys.detail(companyId, invitationId),
    queryFn: () => invitationApi.getInvitation(companyId, invitationId),
    enabled: !!companyId && !!invitationId,
    ...options,
  });
};

/**
 * Create an invitation
 */
export const useCreateInvitation = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<EnrichedInvitation, Error, CreateInvitationDto>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (data: EnrichedInvitation, variables: CreateInvitationDto, context: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto) => invitationApi.createInvitation(companyId, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.lists(companyId) });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Cancel an invitation
 */
export const useCancelInvitation = (
  companyId: string,
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn' | 'onSuccess'> & {
    onSuccess?: (data: void, variables: string, context: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId) =>
      invitationApi.cancelInvitation(companyId, invitationId),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.lists(companyId) });
      queryClient.removeQueries({
        queryKey: invitationKeys.detail(companyId, variables),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Accept an invitation (public)
 */
/**
 * Anonymous preview of an invitation by token — powers the public accept
 * page (company/role context + whether a password is needed).
 */
export const useInvitationPreview = (
  token: string | null,
  options?: Omit<
    UseQueryOptions<{ invitation: invitationApi.InvitationPreview }>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    queryKey: ['invitationPreview', token],
    queryFn: () => invitationApi.getInvitationByToken(token!),
    enabled: !!token,
    retry: false,
    ...options,
  });
};

export const useAcceptInvitation = (
  options?: Omit<
    UseMutationOptions<{ membership: EnrichedInvitation }, Error, invitationApi.AcceptInvitationInput>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: { membership: EnrichedInvitation },
      variables: invitationApi.AcceptInvitationInput,
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invitationApi.acceptInvitation,
    onSuccess: (data, variables, context) => {
      // Invalidate membership caches as a new membership was created
      queryClient.invalidateQueries({ queryKey: membershipKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Decline an invitation (public)
 */
export const useDeclineInvitation = (
  options?: Omit<
    UseMutationOptions<void, Error, { token: string; reason?: string }>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: ({ token, reason }) => invitationApi.declineInvitation(token, reason),
    ...options,
  });
};
